import { useState, useMemo, useEffect } from "react";
import { ThemeProvider, CircularProgress } from "@mui/material";
import { LocalizationProvider, MonthCalendar } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import ModalSkeleton from "../../../Components/Skeleton/modal-skeleton";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useViewport } from "../../../Contexts/ViewportContext";

import { useStakeDebit } from "../../../CustomHooks/Queries";
import { useDownloadStatement } from "../../../CustomHooks/Mutations";

import { dispatchAPIErrorToast, getISOFormatDate } from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";
import { theme } from "../../../Utils/theme";

import { ChevronLeft, ChevronRight } from "../../../Assets/Icons";

import "./bank-statements.scss";

export default function renderBankStatements(modalContext) {
  const { updateModalComponent, pushModalStack, setModalClassName, setModalTitle, setModalVariant } = modalContext;

  updateModalComponent(() => <BankStatements />);
  pushModalStack(() => renderBankStatements(modalContext));
  setModalTitle("Bank Statements");
  setModalVariant(modalVariants.fullScreenDialog);
  setModalClassName("bank-statements-modal");
}

const BankStatements = () => {
  const [selectedDate, setSelectedDate] = useState(moment().startOf("month"));

  const { width } = useViewport();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  useEffect(() => {
    if (!isLoadingStakeDebit) {
      const accountCreatedMonth = moment(stakeDebit?.data?.data?.created_date).startOf("month");
      const currentMonth = moment().startOf("month");

      if (selectedDate.isBefore(accountCreatedMonth)) {
        setSelectedDate(accountCreatedMonth);
      } else if (currentMonth.isBefore(selectedDate)) {
        setSelectedDate(currentMonth);
      }
    }
  }, [isLoadingStakeDebit, selectedDate, stakeDebit]);

  const { isLoading: isDownloadingStatement, mutate: DownloadStatement } = useDownloadStatement({
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    },
    onError: async (error) => {
      const responseData = await error?.response?.data?.text();
      dispatchAPIErrorToast({
        message: JSON.parse(responseData ?? "{}")?.error,
        key: "downloadStatement",
      });
    },
  });

  const { minDate, maxDate } = useMemo(() => {
    return { minDate: moment(stakeDebit?.data?.data?.created_date), maxDate: moment().startOf("month") };
  }, [stakeDebit]);

  const handleYearChange = (direction) => {
    setSelectedDate((prev) => {
      const newDate = prev.clone().add(direction, "year");
      return newDate;
    });
  };

  const handleMonthSelect = (date) => {
    setSelectedDate(date);
  };

  const handleViewStatement = () => {
    DownloadStatement({ date: selectedDate.format("YYYY-MM-DD") });
  };

  return isLoadingStakeDebit ? (
    <ModalSkeleton />
  ) : (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment} dateFormats={{ monthShort: `MMM${width > 400 ? "M" : ""}` }}>
        <div className="modal-dialog-content">
          <div className="bank-statements-picker">
            <div className="year-navigation">
              <button
                className="nav-button"
                onClick={() => handleYearChange(-1)}
                disabled={selectedDate?.year() <= minDate?.year()}
              >
                <ChevronLeft />
              </button>
              <span className="year-display">{selectedDate?.format("YYYY")}</span>
              <button
                className="nav-button"
                onClick={() => handleYearChange(1)}
                disabled={selectedDate?.year() >= maxDate?.year()}
              >
                <ChevronRight />
              </button>
            </div>
            <MonthCalendar
              value={selectedDate}
              onChange={handleMonthSelect}
              minDate={minDate}
              maxDate={maxDate}
              sx={monthCalendarSxProp}
            />
          </div>
        </div>
        <div className="action-container">
          <PrimaryButton onClick={handleViewStatement} disabled={isDownloadingStatement}>
            View Statement {isDownloadingStatement && <CircularProgress />}
          </PrimaryButton>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const monthCalendarSxProp = {
  padding: "0",
  width: "100%",
  gap: "12px",

  "& .MuiPickersMonth-root": {
    flexBasis: "calc(33.33% - 8px)",

    "& .MuiPickersMonth-monthButton": {
      font: "normal normal 500 13px/18px Poppins, sans-serif",
      color: "var(--mist)",
      borderRadius: "var(--rounded-sm)",
      border: "var(--card-border)",
      height: "64px",
      padding: "8px 16px",
      width: "100%",
      margin: "0",
      opacity: 1,

      "&:hover:not(.Mui-selected):not(.Mui-disabled)": {
        backgroundColor: "rgb(from var(--mist) r g b / 6%)",
      },

      "&.Mui-selected": {
        backgroundColor: "rgb(from var(--primary-400) r g b / 8%)",
        color: "var(--primary-400)",
        borderColor: "var(--primary-400)",
      },

      "&.Mui-disabled": {
        borderColor: "rgb(from var(--mist) r g b / 20%)",
        opacity: 0.6,
      },
    },
  },
};
